import { useState } from "react";
import { Loader2, CheckCircle } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import OptionGroup from "./OptionGroup";
import { CLIM_FIELDS, buildClimQualification } from "./verticalFields";
import { toast } from "sonner";
import { canSubmitNow, markSubmitted } from "./submitGuard";

const LeadFormClim = ({ onSubmit }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    prenom: "",
    nom: "",
    telephone: "",
    email: "",
    adresse: "",
    code_postal: "",
    type_logement: "",
    nb_pieces: "",
    chauffage_actuel: "",
    delai: "",
    rgpd_consent: false,
  });

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const canSubmit =
    formData.prenom &&
    formData.nom &&
    formData.telephone &&
    formData.email &&
    formData.adresse &&
    formData.code_postal &&
    CLIM_FIELDS.every((f) => formData[f.field]) &&
    formData.rgpd_consent;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || loading) return;
    if (!canSubmitNow()) {
      toast.info("Votre demande a déjà été envoyée. Merci de patienter une minute.");
      return;
    }

    setLoading(true);
    try {
      await onSubmit({
        prenom: formData.prenom,
        nom: formData.nom,
        telephone: formData.telephone,
        email: formData.email,
        adresse: formData.adresse,
        code_postal: formData.code_postal,
        ...buildClimQualification(formData),
        rgpd_consent: formData.rgpd_consent,
        vertical: "climatisation",
        page_url: window.location.href,
        source: "google_ads_climatisation",
      });
      markSubmitted();
    } catch {
      // Error handled in parent
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="form" className="py-16 md:py-24 bg-slate-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
            Recevez votre devis clim gratuit sous 24h
          </h2>
          <p className="text-base sm:text-lg text-slate-600">
            2 minutes suffisent — sans engagement
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 p-5 sm:p-8 lg:p-10">
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="clim-lead-form">
            {CLIM_FIELDS.map((f) => (
              <OptionGroup
                key={f.field}
                label={f.label}
                options={f.options}
                value={formData[f.field]}
                onChange={(v) => updateField(f.field, v)}
                testPrefix={f.testPrefix}
              />
            ))}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              <div>
                <Label htmlFor="clim-prenom">Prénom *</Label>
                <Input
                  id="clim-prenom"
                  type="text"
                  value={formData.prenom}
                  onChange={(e) => updateField("prenom", e.target.value)}
                  placeholder="Jean"
                  required
                  className="mt-1.5"
                  data-testid="input-prenom"
                />
              </div>
              <div>
                <Label htmlFor="clim-nom">Nom *</Label>
                <Input
                  id="clim-nom"
                  type="text"
                  value={formData.nom}
                  onChange={(e) => updateField("nom", e.target.value)}
                  placeholder="Dupont"
                  required
                  className="mt-1.5"
                  data-testid="input-nom"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="clim-telephone">Téléphone *</Label>
              <Input
                id="clim-telephone"
                type="tel"
                value={formData.telephone}
                onChange={(e) => updateField("telephone", e.target.value)}
                placeholder="0475 12 34 56"
                required
                className="mt-1.5"
                data-testid="input-telephone"
              />
            </div>

            <div>
              <Label htmlFor="clim-email">Email *</Label>
              <Input
                id="clim-email"
                type="email"
                value={formData.email}
                onChange={(e) => updateField("email", e.target.value)}
                placeholder="jean.dupont@email.be"
                required
                className="mt-1.5"
                data-testid="input-email"
              />
            </div>

            <div>
              <Label htmlFor="clim-adresse">Adresse *</Label>
              <Input
                id="clim-adresse"
                type="text"
                value={formData.adresse}
                onChange={(e) => updateField("adresse", e.target.value)}
                placeholder="Rue de la Gare 12"
                required
                className="mt-1.5"
                data-testid="input-adresse"
              />
            </div>

            <div>
              <Label htmlFor="clim-code-postal">Code postal *</Label>
              <Input
                id="clim-code-postal"
                type="text"
                value={formData.code_postal}
                onChange={(e) => updateField("code_postal", e.target.value)}
                placeholder="7000"
                required
                className="mt-1.5"
                data-testid="input-code-postal"
              />
            </div>

            <div className="flex items-start gap-3 pt-2">
              <Checkbox
                id="clim-rgpd"
                checked={formData.rgpd_consent}
                onCheckedChange={(checked) => updateField("rgpd_consent", checked)}
                data-testid="rgpd-checkbox"
              />
              <label htmlFor="clim-rgpd" className="text-sm text-slate-600 leading-relaxed cursor-pointer">
                J'accepte que mes données soient utilisées pour me recontacter dans le
                cadre de ma demande de devis.{" "}
                <a href="/privacy" className="text-amber-600 hover:underline">
                  Politique de confidentialité
                </a>
              </label>
            </div>

            <div className="pt-2">
              <Button
                type="submit"
                disabled={!canSubmit || loading}
                className="w-full bg-amber-500 hover:bg-amber-600 text-white px-8 py-6 text-base sm:text-lg font-semibold"
                data-testid="submit-lead-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Envoi...
                  </>
                ) : (
                  "Recevoir mon devis gratuit"
                )}
              </Button>

              <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 mt-4 text-xs sm:text-sm text-slate-500">
                <span className="flex items-center gap-1">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Réponse sous 24h ouvrées
                </span>
                <span className="flex items-center gap-1">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Sans engagement
                </span>
                <span className="flex items-center gap-1">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Données non revendues
                </span>
              </div>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default LeadFormClim;
